import { useCallback, useEffect, useRef, useState } from "react";
import Navbar from "../../components/Navbar";
import { BASE_URL } from "../../const";

function authHeader() {
  const t = localStorage.getItem("token");
  if (!t) return {};
  return { Authorization: t.startsWith("Bearer ") || t.startsWith("Token ") ? t : `Token ${t}` };
}

// ── constants ────────────────────────────────────────────────────────────────
const VENDOR_STATUSES   = ["pending", "open", "vendor_confirmed", "partially_dispatched", "fully_dispatched", "cancelled"];
const TRACKING_STATUSES = ["not_dispatched", "in_transit", "partially_received", "fully_received", "grn_done"];
const FINAL_STATUSES    = ["open", "ongoing", "completed", "closed", "cancelled"];

const STATUS_COLORS = {
  pending:              { bg: "#fff7ed", text: "#c2410c", border: "#fed7aa" },
  open:                 { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
  vendor_confirmed:     { bg: "#f0fdf4", text: "#166534", border: "#bbf7d0" },
  partially_dispatched: { bg: "#fefce8", text: "#854d0e", border: "#fef08a" },
  fully_dispatched:     { bg: "#f0fdf4", text: "#166534", border: "#bbf7d0" },
  cancelled:            { bg: "#fef2f2", text: "#dc2626", border: "#fecaca" },
  not_dispatched:       { bg: "#f8fafc", text: "#64748b", border: "#e2e8f0" },
  in_transit:           { bg: "#eff6ff", text: "#1d4ed8", border: "#bfdbfe" },
  partially_received:   { bg: "#fefce8", text: "#854d0e", border: "#fef08a" },
  fully_received:       { bg: "#f0fdf4", text: "#166534", border: "#bbf7d0" },
  grn_done:             { bg: "#f0fdf4", text: "#166534", border: "#bbf7d0" },
  ongoing:              { bg: "#fefce8", text: "#854d0e", border: "#fef08a" },
  completed:            { bg: "#f0fdf4", text: "#166534", border: "#bbf7d0" },
  closed:               { bg: "#f8fafc", text: "#64748b", border: "#e2e8f0" },
  draft:                { bg: "#f8fafc", text: "#64748b", border: "#e2e8f0" },
};

// ── shared styles ────────────────────────────────────────────────────────────
const inputStyle  = { width: "100%", padding: "7px 10px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, color: "#111827", outline: "none", boxSizing: "border-box", background: "#fff" };
const sectionHead = { fontSize: 11, fontWeight: 700, color: "#ea580c", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10, paddingBottom: 6, borderBottom: "1px solid #fed7aa" };
const btnPrimary  = { padding: "7px 16px", border: "none", borderRadius: 8, background: "#ea580c", color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" };
const btnGhost    = { padding: "7px 12px", border: "1px solid #e2e8f0", borderRadius: 8, background: "#fff", fontSize: 12, color: "#374151", cursor: "pointer", whiteSpace: "nowrap" };
const card        = { background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", padding: "18px 20px" };

// ── helpers ──────────────────────────────────────────────────────────────────
function genRequestCode() {
  const d = new Date();

  const yy = String(d.getFullYear()).slice(-2);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");

  const hh = String(d.getHours()).padStart(2, "0");
  const min = String(d.getMinutes()).padStart(2, "0");

  return `REQ-${yy}${mm}${dd}-${hh}${min}`;
}

function fmt(date) { return date ? date : "—"; }

function StatusBadge({ value }) {
  const c = STATUS_COLORS[value] || { bg: "#f8fafc", text: "#64748b", border: "#e2e8f0" };
  return (
    <span style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`, borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600, textTransform: "capitalize", whiteSpace: "nowrap" }}>
      {(value || "—").replace(/_/g, " ")}
    </span>
  );
}

function FormField({ label, required, children }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: "#374151" }}>
        {label}{required && <span style={{ color: "#ef4444" }}> *</span>}
      </label>
      {children}
    </div>
  );
}

// ── PartySelector — combobox with avatar initials + address card ──────────────
function PartySelector({ parties, partyTypeFilter, excludePartyType, selected, onSelect, label }) {
  const [open,  setOpen]  = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    function handler(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = parties
    .filter(p => !partyTypeFilter || p.party_type === partyTypeFilter)
    .filter(p => !excludePartyType || p.party_type !== excludePartyType)
    .filter(p => {
      if (!query.trim()) return true;
      const q = query.toLowerCase();
      return (
        (p.party_name || "").toLowerCase().includes(q) ||
        (p.party_code || "").toLowerCase().includes(q)
      );
    })
    .slice(0, 60);

  function initial(name) { return (name || "?")[0].toUpperCase(); }

  function getShippingAddress(p) {
    if (!p) return null;
    const useBilling = p.same_as_billing;
    const addr   = (!useBilling && p.shipping_address)  || p.billing_address;
    const street2 = (!useBilling && p.shipping_street2) || p.billing_street2;
    const city   = (!useBilling && p.shipping_city)     || p.billing_city;
    const state  = (!useBilling && p.shipping_state)    || p.billing_state;
    const pin    = (!useBilling && p.shipping_pincode)  || p.billing_pincode;
    if (!addr && !city) return null;
    return { addr, street2, city, state, pin };
  }

  const address = selected ? getShippingAddress(selected) : null;

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>

      {/* ── Trigger button ── */}
      <button
        type="button"
        onClick={() => setOpen(s => !s)}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "8px 12px", border: `1.5px solid ${open ? "#6366f1" : "#e2e8f0"}`, borderRadius: 8,
          background: "#fff", cursor: "pointer", fontSize: 13, gap: 8, textAlign: "left",
          boxShadow: open ? "0 0 0 3px rgba(99,102,241,0.12)" : "none", transition: "all 0.15s",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 9, flex: 1, minWidth: 0 }}>
          {selected ? (
            <>
              <div style={{ width: 28, height: 28, borderRadius: "50%", background: "#6366f1", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 700, flexShrink: 0 }}>
                {initial(selected.party_name)}
              </div>
              <span style={{ fontWeight: 600, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {selected.party_name}
              </span>
            </>
          ) : (
            <span style={{ color: "#9ca3af" }}>Select or add a {label}…</span>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}>
          {selected && (
            <span
              onClick={e => { e.stopPropagation(); onSelect(null); setOpen(false); }}
              style={{ width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center", color: "#9ca3af", cursor: "pointer", fontSize: 13, borderRadius: 4 }}
              onMouseEnter={e => e.currentTarget.style.color = "#dc2626"}
              onMouseLeave={e => e.currentTarget.style.color = "#9ca3af"}
            >✕</span>
          )}
          {/* search icon */}
          <svg width="15" height="15" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth={2}>
            <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" d="m21 21-4.35-4.35" />
          </svg>
        </div>
      </button>

      {/* ── Dropdown ── */}
      {open && (
        <div style={{
          position: "absolute", left: 0, right: 0, top: "calc(100% + 4px)", zIndex: 300,
          background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10,
          boxShadow: "0 10px 30px rgba(0,0,0,0.13)", overflow: "hidden",
        }}>
          {/* Search bar */}
          <div style={{ padding: "8px 12px", borderBottom: "1px solid #f1f5f9", display: "flex", alignItems: "center", gap: 8, background: "#fafafa" }}>
            <svg width="13" height="13" fill="none" viewBox="0 0 24 24" stroke="#9ca3af" strokeWidth={2} style={{ flexShrink: 0 }}>
              <circle cx="11" cy="11" r="8" /><path strokeLinecap="round" d="m21 21-4.35-4.35" />
            </svg>
            <input
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search…"
              autoFocus
              style={{ border: "none", outline: "none", flex: 1, fontSize: 13, color: "#111827", background: "transparent" }}
            />
          </div>

          {/* List */}
          <div style={{ maxHeight: 230, overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "12px 16px", color: "#9ca3af", fontSize: 12 }}>No {label} found.</div>
            ) : filtered.map(p => (
              <div
                key={p.id}
                onClick={() => { onSelect(p); setOpen(false); setQuery(""); }}
                style={{ padding: "9px 14px", cursor: "pointer", borderBottom: "1px solid #f9fafb", display: "flex", alignItems: "center", gap: 10, background: selected?.id === p.id ? "#eef2ff" : "#fff" }}
                onMouseEnter={e => { if (selected?.id !== p.id) e.currentTarget.style.background = "#f8fafc"; }}
                onMouseLeave={e => { e.currentTarget.style.background = selected?.id === p.id ? "#eef2ff" : "#fff"; }}
              >
                {/* Avatar */}
                <div style={{ width: 32, height: 32, borderRadius: "50%", background: selected?.id === p.id ? "#6366f1" : "#e0e7ff", color: selected?.id === p.id ? "#fff" : "#6366f1", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                  {initial(p.party_name)}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 13, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.party_name}</div>
                  <div style={{ fontSize: 11, color: "#9ca3af", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {[p.party_code, p.party_type].filter(Boolean).join(" · ")}
                  </div>
                </div>
                {selected?.id === p.id && (
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Address card (shown when party selected and has address data) ── */}
      {selected && address && (
        <div style={{ marginTop: 8, padding: "10px 14px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 5 }}>
            {selected.same_as_billing ? "Billing Address (ships here)" : "Shipping Address"}
          </div>
          <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.7 }}>
            {address.addr   && <div>{address.addr}</div>}
            {address.street2 && <div>{address.street2}</div>}
            {(address.city || address.state) && (
              <div>{[address.city, address.state].filter(Boolean).join(", ")}</div>
            )}
            {address.pin && (
              <div style={{ marginTop: 2 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: "#6b7280", background: "#f1f5f9", border: "1px solid #e2e8f0", borderRadius: 4, padding: "1px 7px" }}>
                  PIN {address.pin}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── SKU Search + multi-line selector ─────────────────────────────────────────
function SkuSearchSection({ skuLines, setSkuLines, editMode }) {
  const [query, setQuery]             = useState("");
  const [skuType, setSkuType]         = useState("");
  const [results, setResults]         = useState([]);
  const [loading, setLoading]         = useState(false);
  const [showResults, setShowResults] = useState(false);

  async function doSearch() {
    const q = query.trim();
    if (!q) return;
    setLoading(true);
    try {
      const p = new URLSearchParams({ search: q, page_size: 20 });
      if (skuType) p.set("sku_type", skuType);
      const res  = await fetch(`${BASE_URL}/sku/?${p}`, { headers: authHeader() });
      const data = await res.json();
      setResults(data?.results || []);
      setShowResults(true);
    } catch { setResults([]); setShowResults(true); }
    finally { setLoading(false); }
  }

  const alreadyAdded = sku => skuLines.some(l => l.sku === sku);

  function addSku(item) {
    if (alreadyAdded(item.sku)) return;
    setSkuLines(prev => [...prev, { sku: item.sku, sku_name: item.sku_name || "", sku_object: item.sku || "", qty: 1 }]);
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", gap: 6 }}>
        <input
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={e => e.key === "Enter" && doSearch()}
          placeholder="Search SKU or name…"
          style={{ ...inputStyle, flex: 1 }}
        />
        <select value={skuType} onChange={e => setSkuType(e.target.value)} style={{ ...inputStyle, width: 120, flex: "none" }}>
          <option value="">All types</option>
          <option value="single_sku">Single</option>
          <option value="combo_product">Bundle</option>
        </select>
        <button onClick={doSearch} disabled={loading} style={btnPrimary}>{loading ? "…" : "Search"}</button>
      </div>

      {showResults && (
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, maxHeight: 200, overflowY: "auto", boxShadow: "0 4px 12px rgba(0,0,0,0.08)" }}>
          {results.length === 0 ? (
            <div style={{ padding: "10px 14px", color: "#9ca3af", fontSize: 12 }}>No SKUs found.</div>
          ) : results.map(item => {
            const added = alreadyAdded(item.sku);
            return (
              <div key={item.id || item.sku} style={{ display: "flex", alignItems: "center", padding: "8px 14px", borderBottom: "1px solid #f1f5f9", gap: 10, background: added ? "#f8fafc" : "#fff" }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 600, fontSize: 13, fontFamily: "monospace", color: "#111827" }}>{item.sku}</span>
                  <span style={{ fontSize: 12, color: "#6b7280", marginLeft: 8 }}>{item.sku_name}</span>
                </div>
                <span style={{ fontSize: 11, color: "#ea580c", fontWeight: 600, whiteSpace: "nowrap" }}>
                  {item.sku_type === "combo_product" ? "Bundle" : item.sku_type === "single_sku" ? "Single" : item.sku_type}
                </span>
                <button
                  onClick={() => !added && addSku(item)}
                  disabled={added}
                  style={{ padding: "3px 10px", border: `1px solid ${added ? "#e2e8f0" : "#ea580c"}`, borderRadius: 6, background: added ? "#f8fafc" : "#fff7ed", color: added ? "#9ca3af" : "#ea580c", fontSize: 12, cursor: added ? "default" : "pointer" }}
                >
                  {added ? "Added" : "+ Add"}
                </button>
              </div>
            );
          })}
        </div>
      )}

      {skuLines.length === 0 ? (
        <div style={{ padding: "14px", textAlign: "center", color: "#9ca3af", fontSize: 12, border: "1px dashed #e2e8f0", borderRadius: 8 }}>
          Search and add SKUs above.
        </div>
      ) : (
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, overflow: "hidden" }}>
          <div style={{ background: "#f8fafc", padding: "6px 12px", display: "grid", gridTemplateColumns: "130px 1fr 80px 32px", gap: 8, fontSize: 11, fontWeight: 700, color: "#6b7280", textTransform: "uppercase" }}>
            <span>SKU</span><span>Name</span><span style={{ textAlign: "center" }}>Qty *</span><span></span>
          </div>
          {skuLines.map((line, idx) => (
            <div key={idx} style={{ padding: "7px 12px", display: "grid", gridTemplateColumns: "130px 1fr 80px 32px", gap: 8, alignItems: "center", borderTop: "1px solid #f1f5f9" }}>
              <span style={{ fontSize: 12, fontWeight: 600, fontFamily: "monospace", color: "#374151", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{line.sku}</span>
              <span style={{ fontSize: 12, color: "#6b7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{line.sku_name || "—"}</span>
              <input
                type="number" min="1"
                value={line.qty}
                onChange={e => setSkuLines(prev => prev.map((l, i) => i === idx ? { ...l, qty: e.target.value } : l))}
                style={{ ...inputStyle, padding: "4px 6px", textAlign: "center" }}
              />
              {(!editMode || skuLines.length > 1) && (
                <button
                  onClick={() => setSkuLines(prev => prev.filter((_, i) => i !== idx))}
                  style={{ width: 28, height: 28, border: "1px solid #fecaca", borderRadius: 6, background: "#fef2f2", color: "#dc2626", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", justifyContent: "center" }}
                >✕</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Detail View (full-screen overlay) ────────────────────────────────────────
function DetailView({ requestCode, onClose }) {
  const [requirements, setRequirements] = useState([]);
  const [invoices, setInvoices]         = useState({});
  const [loading, setLoading]           = useState(true);

  useEffect(() => {
    if (!requestCode) return;
    setLoading(true);
    async function load() {
      try {
        const res  = await fetch(`${BASE_URL}/vendor/requirements/?search=${encodeURIComponent(requestCode)}&page_size=200`, { headers: authHeader() });
        const data = await res.json();
        const reqs = (data?.results || []).filter(r => r.request_code === requestCode);
        setRequirements(reqs);

        const invMap = {};
        await Promise.all(reqs.map(async req => {
          const r2   = await fetch(`${BASE_URL}/vendor/invoices/?requirement=${req.id}&page_size=200`, { headers: authHeader() });
          const d2   = await r2.json();
          invMap[req.id] = d2?.results || [];
        }));
        setInvoices(invMap);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [requestCode]);

  const first     = requirements[0] || {};
  const totalQty  = requirements.reduce((s, r) => s + (Number(r.required_qty) || 0), 0);
  const allInvoices = Object.values(invoices).flat();

  return (
    <div style={{ position: "fixed", inset: 0, background: "#f8fafc", zIndex: 200, overflowY: "auto" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "20px 24px" }}>

        {/* Back header */}
        <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 24 }}>
          <button onClick={onClose} style={{ display: "flex", alignItems: "center", gap: 6, ...btnGhost }}>
            <svg width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" /></svg>
            Back
          </button>
          <div>
            <h1 style={{ margin: 0, fontSize: 20, fontWeight: 700, color: "#111827" }}>{requestCode}</h1>
            {first.vendor_name && <p style={{ margin: 0, fontSize: 13, color: "#6b7280" }}>Vendor: {first.vendor_name}</p>}
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: 60, color: "#9ca3af", fontSize: 14 }}>Loading…</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>

            {/* ── Request Details ── */}
            <div style={card}>
              <p style={sectionHead}>Request Details</p>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 16 }}>
                {[
                  ["Request Code",    first.request_code],
                  ["Vendor",          first.vendor_name || "—"],
                  ["Delivery Party",  first.delivery_party_name || "—"],
                  ["Raised By",       first.raised_by || "—"],
                  ["Created Date",    fmt(first.requirement_date)],
                  ["Target Ship",     fmt(first.target_ship_date)],
                  ["Expected GRN",    fmt(first.expected_grn_date)],
                  ["GRN Date",        fmt(first.grn_date)],
                  ["Total SKUs",      requirements.length],
                  ["Total Qty",       totalQty],
                ].map(([lbl, val]) => (
                  <div key={lbl}>
                    <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>{lbl}</div>
                    <div style={{ fontSize: 13, fontWeight: 500, color: "#111827" }}>{val}</div>
                  </div>
                ))}
              </div>
              {first.remarks && (
                <div style={{ marginTop: 14, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, fontSize: 13, color: "#374151" }}>
                  <span style={{ fontWeight: 600, color: "#6b7280" }}>Remarks: </span>{first.remarks}
                </div>
              )}
            </div>

            {/* ── Status ── */}
            <div style={card}>
              <p style={sectionHead}>Status</p>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
                <div>
                  <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", marginBottom: 4 }}>Vendor Status</div>
                  <StatusBadge value={first.vendor_status} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", marginBottom: 4 }}>Tracking Status</div>
                  <StatusBadge value={first.tracking_status} />
                </div>
                <div>
                  <div style={{ fontSize: 11, color: "#9ca3af", fontWeight: 600, textTransform: "uppercase", marginBottom: 4 }}>Requirement Status</div>
                  <StatusBadge value={first.final_requirement_status} />
                </div>
              </div>
            </div>

            {/* ── SKU Lines ── */}
            <div style={card}>
              <p style={sectionHead}>SKU Lines ({requirements.length})</p>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                      {["SKU", "SKU Name", "Required Qty", "Dispatched Boxes", "Total Dispatched Qty"].map(h => (
                        <th key={h} style={{ padding: "8px 14px", textAlign: "left", fontWeight: 600, color: "#374151", fontSize: 12, whiteSpace: "nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {requirements.map(req => {
                      const reqInvoices  = invoices[req.id] || [];
                      const dispatchedQty = reqInvoices.reduce((s, inv) => s + (Number(inv.qty) || 0), 0);
                      return (
                        <tr key={req.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td style={{ padding: "9px 14px", fontFamily: "monospace", fontWeight: 600, color: "#374151" }}>{req.sku}</td>
                          <td style={{ padding: "9px 14px", color: "#6b7280" }}>{req.sku_name || "—"}</td>
                          <td style={{ padding: "9px 14px", fontWeight: 600, color: "#111827", textAlign: "right" }}>{req.required_qty}</td>
                          <td style={{ padding: "9px 14px", textAlign: "right", color: "#6b7280" }}>{reqInvoices.length}</td>
                          <td style={{ padding: "9px 14px", textAlign: "right", fontWeight: 600, color: dispatchedQty >= req.required_qty ? "#166534" : "#111827" }}>{dispatchedQty}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* ── Invoice / Dispatch Details ── */}
            <div style={card}>
              <p style={sectionHead}>Invoice & Dispatch Details ({allInvoices.length} boxes)</p>
              {allInvoices.length === 0 ? (
                <div style={{ padding: "20px", textAlign: "center", color: "#9ca3af", fontSize: 13 }}>No invoices linked yet.</div>
              ) : (
                <div style={{ overflowX: "auto" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: "#f8fafc", borderBottom: "1px solid #e2e8f0" }}>
                        {["Invoice No.", "Box ID", "SKU", "Qty", "AWB", "Courier", "Invoice Date", "Dispatch Date", "Exp. Delivery", "Actual Delivery", "Tracking Status"].map(h => (
                          <th key={h} style={{ padding: "8px 12px", textAlign: "left", fontWeight: 600, color: "#374151", whiteSpace: "nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {allInvoices.map((inv, i) => (
                        <tr key={inv.id || i} style={{ borderBottom: "1px solid #f1f5f9", background: i % 2 === 0 ? "#fff" : "#fafafa" }}>
                          <td style={{ padding: "8px 12px", fontFamily: "monospace", fontWeight: 600, color: "#374151" }}>{inv.invoice_number}</td>
                          <td style={{ padding: "8px 12px", color: "#6b7280" }}>{inv.box_id}</td>
                          <td style={{ padding: "8px 12px", fontFamily: "monospace" }}>{inv.sku}</td>
                          <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 600 }}>{inv.qty}</td>
                          <td style={{ padding: "8px 12px", color: "#374151" }}>{inv.awb_number || "—"}</td>
                          <td style={{ padding: "8px 12px", color: "#6b7280" }}>{inv.courier_partner || "—"}</td>
                          <td style={{ padding: "8px 12px", color: "#6b7280", whiteSpace: "nowrap" }}>{fmt(inv.invoice_date)}</td>
                          <td style={{ padding: "8px 12px", color: "#6b7280", whiteSpace: "nowrap" }}>{fmt(inv.dispatch_date)}</td>
                          <td style={{ padding: "8px 12px", color: "#6b7280", whiteSpace: "nowrap" }}>{fmt(inv.expected_delivery_date)}</td>
                          <td style={{ padding: "8px 12px", color: "#6b7280", whiteSpace: "nowrap" }}>{fmt(inv.actual_delivery_date)}</td>
                          <td style={{ padding: "8px 12px" }}><StatusBadge value={inv.tracking_status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
const EMPTY_FORM = {
  target_ship_date: "", expected_grn_date: "", grn_date: "",
  raised_by: "", remarks: "",
};

export default function RequirementsSummary() {
  const [rows, setRows]             = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [pageSize]                  = useState(100);
  const [loading, setLoading]       = useState(false);
  const [error, setError]           = useState("");
  const [filters, setFilters]       = useState({ search: "", sku: "", vendor_status: "", tracking_status: "", final_requirement_status: "" });

  // Detail view
  const [detailCode, setDetailCode] = useState(null);

  // Create / Edit modal
  const [modalOpen, setModalOpen]   = useState(false);
  const [editRow, setEditRow]       = useState(null);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [skuLines, setSkuLines]     = useState([]);
  const [selectedVendor, setSelectedVendor]     = useState(null);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [saving, setSaving]         = useState(false);
  const [deleteId, setDeleteId]     = useState(null);
  const [deleting, setDeleting]     = useState(false);

  // Pre-loaded parties for create modal
  const [allParties, setAllParties]     = useState([]);
  const [partiesLoading, setPartiesLoading] = useState(false);

  // ── fetch table data ───────────────────────────────────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = new URLSearchParams({ page, page_size: pageSize });
      if (filters.search)                   params.set("search",                   filters.search);
      if (filters.sku)                      params.set("sku",                      filters.sku);
      if (filters.vendor_status)            params.set("vendor_status",            filters.vendor_status);
      if (filters.tracking_status)          params.set("tracking_status",          filters.tracking_status);
      if (filters.final_requirement_status) params.set("final_requirement_status", filters.final_requirement_status);
      const res  = await fetch(`${BASE_URL}/vendor/requirements/?${params}`, { headers: authHeader() });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.message || "Failed to load");
      setRows(data?.results || data?.data?.results || data?.data || []);
      setTotal(data?.count || data?.data?.count || 0);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [page, pageSize, filters]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── load all parties ───────────────────────────────────────────────────────
  async function loadAllParties() {
    setPartiesLoading(true);
    try {
      const res  = await fetch(`${BASE_URL}/vendor/parties/?page_size=300&is_active=true`, { headers: authHeader() });
      const data = await res.json();
      setAllParties(data?.results || []);
    } catch { setAllParties([]); }
    finally { setPartiesLoading(false); }
  }

  // ── open create ────────────────────────────────────────────────────────────
  function openCreate() {
    setEditRow(null);
    setForm(EMPTY_FORM);
    setSkuLines([]);
    setSelectedVendor(null);
    setSelectedDelivery(null);
    setModalOpen(true);
    loadAllParties();
  }

  function openEdit(row) {
    setEditRow(row);
    setForm({
      target_ship_date: row.target_ship_date || "",
      expected_grn_date: row.expected_grn_date || "",
      grn_date: row.grn_date || "",
      raised_by: row.raised_by || "",
      remarks: row.remarks || "",
    });
    setSkuLines([{ sku: row.sku || "", sku_name: row.sku_name || "", sku_object: row.sku_object || "", qty: row.required_qty || 1 }]);
    setSelectedVendor(row.vendor_id ? { id: row.vendor_id, party_name: row.vendor_name || "", party_code: "" } : (row.vendor_name ? { id: null, party_name: row.vendor_name, party_code: "" } : null));
    setSelectedDelivery(row.delivery_party_id ? { id: row.delivery_party_id, party_name: row.delivery_party_name || "", party_code: "" } : (row.delivery_party_name ? { id: null, party_name: row.delivery_party_name, party_code: "" } : null));
    setModalOpen(true);
    loadAllParties();
  }

  // ── save ───────────────────────────────────────────────────────────────────
  async function handleSave() {
    if (skuLines.length === 0)       { alert("Add at least one SKU."); return; }
    if (!selectedVendor?.party_name) { alert("Select a vendor."); return; }
    if (!selectedDelivery?.party_name) { alert("Select a delivery party."); return; }

    const dateOrNull = v => (v && v.trim() ? v : null);

    setSaving(true);
    try {
      const sharedFields = {
        target_ship_date:  dateOrNull(form.target_ship_date),
        expected_grn_date: dateOrNull(form.expected_grn_date),
        grn_date:          dateOrNull(form.grn_date),
        raised_by:  form.raised_by,
        remarks:    form.remarks,
        vendor_id:          selectedVendor?.id   || null,
        vendor_name:        selectedVendor?.party_name || "",
        delivery_party_id:  selectedDelivery?.id || null,
        delivery_party_name: selectedDelivery?.party_name || "",
      };

      if (editRow) {
        const line = skuLines[0];
        const payload = { ...sharedFields, sku: line.sku, sku_name: line.sku_name, sku_object: line.sku_object, required_qty: Number(line.qty) };
        const res  = await fetch(`${BASE_URL}/vendor/requirements/${editRow.id}/`, {
          method: "PUT",
          headers: { ...authHeader(), "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data?.message || JSON.stringify(data?.errors || data));

      } else {
        const code  = genRequestCode();
        if (skuLines.length === 1) {
          const line    = skuLines[0];
          const payload = { ...sharedFields, request_code: code, sku: line.sku, sku_name: line.sku_name, sku_object: line.sku_object, required_qty: Number(line.qty) };
          const res     = await fetch(`${BASE_URL}/vendor/requirements/create/`, {
            method: "POST",
            headers: { ...authHeader(), "Content-Type": "application/json" },
            body: JSON.stringify(payload),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data?.message || JSON.stringify(data?.errors || data));
        } else {
          const items = skuLines.map(line => ({
            ...sharedFields,
            request_code: code,
            sku: line.sku, sku_name: line.sku_name, sku_object: line.sku_object, required_qty: Number(line.qty),
          }));
          const res  = await fetch(`${BASE_URL}/vendor/requirements/bulk-create/`, {
            method: "POST",
            headers: { ...authHeader(), "Content-Type": "application/json" },
            body: JSON.stringify(items),
          });
          const data = await res.json();
          if (!res.ok) throw new Error(data?.message || JSON.stringify(data?.errors || data));
          const failed = data?.data?.failed || 0;
          if (failed > 0) {
            const errs = (data?.data?.errors || []).map(e => e.remarks || JSON.stringify(e)).join("; ");
            throw new Error(`${failed} rows failed: ${errs}`);
          }
        }
      }
      setModalOpen(false);
      fetchData();
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    setDeleting(true);
    try {
      const res = await fetch(`${BASE_URL}/vendor/requirements/${id}/`, { method: "DELETE", headers: authHeader() });
      if (!res.ok) throw new Error("Delete failed");
      setDeleteId(null); fetchData();
    } catch (e) { alert(e.message); }
    finally { setDeleting(false); }
  }

  // ── group rows by request_code ─────────────────────────────────────────────
  const groupKeys = [];
  const groupMap  = {};
  for (const row of rows) {
    if (!groupMap[row.request_code]) {
      groupMap[row.request_code] = [];
      groupKeys.push(row.request_code);
    }
    groupMap[row.request_code].push(row);
  }

  const f           = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const totalPages  = Math.ceil(total / pageSize) || 1;

  // ── detail view ────────────────────────────────────────────────────────────
  if (detailCode) {
    return <DetailView requestCode={detailCode} onClose={() => setDetailCode(null)} />;
  }

  return (
    <div style={{ minHeight: "100vh", background: "#f8fafc" }}>
      <style>{`
        @media (max-width: 640px) {
          .rs-wrap { padding: 14px 12px !important; }
          .rs-header { flex-wrap: wrap !important; gap: 12px !important; }
          .rs-filters { padding: 10px 12px !important; }
          .rs-filters input, .rs-filters select { width: 100% !important; }
          .rs-count { margin-left: 0 !important; }
          .rs-form-grid { grid-template-columns: 1fr !important; }
          .rs-panel { width: min(660px, 100vw) !important; }
          .rs-panel-footer { flex-direction: column !important; padding: 12px 14px calc(12px + env(safe-area-inset-bottom, 0px)) !important; }
          .rs-panel-footer button { width: 100% !important; justify-content: center !important; }
        }
      `}</style>
      <Navbar />
      <div className="rs-wrap" style={{ maxWidth: 1400, margin: "0 auto", padding: "24px 20px" }}>

        {/* Header */}
        <div className="rs-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
          <div>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111827", margin: 0 }}>Requirements Summary</h1>
            <p style={{ fontSize: 13, color: "#6b7280", marginTop: 2 }}>Track vendor requirements raised by inventory manager</p>
          </div>
          <button onClick={openCreate} style={{ ...btnPrimary, padding: "9px 20px", fontSize: 14 }}>+ New Requirement</button>
        </div>

        {/* Filters */}
        <div className="rs-filters" style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", padding: "14px 16px", marginBottom: 16, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
          <input placeholder="Search code, SKU, vendor…" value={filters.search} onChange={e => { setFilters(p => ({ ...p, search: e.target.value })); setPage(1); }} style={{ ...inputStyle, width: 220 }} />
          <input placeholder="SKU" value={filters.sku} onChange={e => { setFilters(p => ({ ...p, sku: e.target.value })); setPage(1); }} style={{ ...inputStyle, width: 140 }} />
          <select value={filters.vendor_status} onChange={e => { setFilters(p => ({ ...p, vendor_status: e.target.value })); setPage(1); }} style={{ ...inputStyle, width: 180 }}>
            <option value="">All Vendor Status</option>
            {VENDOR_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <select value={filters.tracking_status} onChange={e => { setFilters(p => ({ ...p, tracking_status: e.target.value })); setPage(1); }} style={{ ...inputStyle, width: 180 }}>
            <option value="">All Tracking Status</option>
            {TRACKING_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <select value={filters.final_requirement_status} onChange={e => { setFilters(p => ({ ...p, final_requirement_status: e.target.value })); setPage(1); }} style={{ ...inputStyle, width: 170 }}>
            <option value="">All Req Status</option>
            {FINAL_STATUSES.map(s => <option key={s} value={s}>{s.replace(/_/g, " ")}</option>)}
          </select>
          <button onClick={() => { setFilters({ search: "", sku: "", vendor_status: "", tracking_status: "", final_requirement_status: "" }); setPage(1); }} style={btnGhost}>Clear</button>
          <span className="rs-count" style={{ marginLeft: "auto", fontSize: 12, color: "#9ca3af" }}>{groupKeys.length} groups · {total} rows</span>
        </div>

        {error && <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 8, padding: "10px 14px", color: "#dc2626", fontSize: 13, marginBottom: 12 }}>{error}</div>}

        {/* Grouped Table */}
        <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e2e8f0", overflow: "hidden" }}>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  {["Created Date", "Req Code", "SKUs", "Total Qty", "Vendor Party", "Delivery Party", "Vendor Status", "Tracking", "Req Status", "Actions"].map(h => (
                    <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 600, color: "#374151", whiteSpace: "nowrap", fontSize: 12 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={9} style={{ textAlign: "center", padding: 48, color: "#9ca3af" }}>Loading…</td></tr>
                ) : groupKeys.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign: "center", padding: 48, color: "#9ca3af" }}>No requirements found</td></tr>
                ) : groupKeys.map((code, gi) => {
                  const group    = groupMap[code];
                  const first    = group[0];
                  const totalQty = group.reduce((s, r) => s + (Number(r.required_qty) || 0), 0);
                  const extra    = group.length - 1;
                  return (
                    <tr
                      key={code}
                      style={{ borderBottom: "1px solid #f1f5f9", background: gi % 2 === 0 ? "#fff" : "#fafafa", cursor: "pointer" }}
                      onClick={() => setDetailCode(code)}
                      onMouseEnter={e => e.currentTarget.style.background = "#fff7ed"}
                      onMouseLeave={e => e.currentTarget.style.background = gi % 2 === 0 ? "#fff" : "#fafafa"}
                    >
                      <td style={{ padding: "12px 16px", color: "#6b7280", fontSize: 12, whiteSpace: "nowrap" }}>{fmt(first.requirement_date)}</td>
                      <td style={{ padding: "12px 16px", fontFamily: "monospace", fontWeight: 700, color: "#111827", whiteSpace: "nowrap" }}>{code}</td>
                      <td style={{ padding: "12px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span style={{ fontWeight: 500, color: "#111827", fontSize: 13 }}>{first.sku_name || first.sku || "—"}</span>
                          {extra > 0 && (
                            <span style={{ background: "#f1f5f9", color: "#6b7280", borderRadius: 12, padding: "1px 7px", fontSize: 11, fontWeight: 600 }}>+{extra} more</span>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: "12px 16px", fontWeight: 700, color: "#111827", textAlign: "right" }}>{totalQty}</td>
                      <td style={{ padding: "12px 16px", color: "#374151", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{first.vendor_name || "—"}</td>
                      <td style={{ padding: "12px 16px", color: "#374151", maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{first.delivery_party_name || "—"}</td>
                      <td style={{ padding: "12px 16px" }}><StatusBadge value={first.vendor_status} /></td>
                      <td style={{ padding: "12px 16px" }}><StatusBadge value={first.tracking_status} /></td>
                      <td style={{ padding: "12px 16px" }}><StatusBadge value={first.final_requirement_status} /></td>
                      <td style={{ padding: "12px 16px" }} onClick={e => e.stopPropagation()}>
                        <div style={{ display: "flex", gap: 6 }}>
                          <button onClick={() => openEdit(first)} style={{ padding: "4px 10px", border: "1px solid #e2e8f0", borderRadius: 6, background: "#fff", fontSize: 12, cursor: "pointer" }}>Edit</button>
                          <button onClick={() => setDeleteId(first.id)} style={{ padding: "4px 10px", border: "1px solid #fecaca", borderRadius: 6, background: "#fef2f2", fontSize: 12, cursor: "pointer", color: "#dc2626" }}>Del</button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 16px", borderTop: "1px solid #f1f5f9" }}>
            <span style={{ fontSize: 12, color: "#9ca3af" }}>Page {page} of {totalPages} · {total} total rows</span>
            <div style={{ display: "flex", gap: 6 }}>
              <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} style={{ ...btnGhost, cursor: page <= 1 ? "default" : "pointer" }}>← Prev</button>
              <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} style={{ ...btnGhost, cursor: page >= totalPages ? "default" : "pointer" }}>Next →</button>
            </div>
          </div>
        </div>
      </div>

      {/* Delete confirm */}
      {deleteId && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ background: "#fff", borderRadius: 12, padding: 28, width: 340, boxShadow: "0 20px 60px rgba(0,0,0,0.18)" }}>
            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>Delete Requirement?</h3>
            <p style={{ fontSize: 13, color: "#6b7280", marginTop: 8 }}>This will also delete all linked invoices. Cannot be undone.</p>
            <div style={{ display: "flex", gap: 8, marginTop: 20, justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteId(null)} style={btnGhost}>Cancel</button>
              <button onClick={() => handleDelete(deleteId)} disabled={deleting} style={{ ...btnPrimary, background: "#dc2626" }}>{deleting ? "Deleting…" : "Delete"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit slide-in panel */}
      {modalOpen && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 100, display: "flex", alignItems: "flex-start", justifyContent: "flex-end" }}>
          <div className="rs-panel" style={{ background: "#fff", width: "min(660px, 100vw)", height: "100vh", overflowY: "auto", boxShadow: "-8px 0 40px rgba(0,0,0,0.15)", display: "flex", flexDirection: "column" }}>

            {/* Panel header */}
            <div style={{ padding: "18px 24px", borderBottom: "1px solid #e2e8f0", display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
              <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, color: "#111827" }}>{editRow ? "Edit Requirement" : "New Requirement"}</h2>
              <button onClick={() => setModalOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", padding: 4 }}>
                <svg xmlns="http://www.w3.org/2000/svg" style={{ width: 20, height: 20, color: "#6b7280" }} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div style={{ flex: 1, padding: "20px 24px", display: "flex", flexDirection: "column", gap: 22 }}>

              {/* SKU Details */}
              <div>
                <p style={sectionHead}>SKU Details <span style={{ color: "#ef4444" }}>*</span></p>
                <SkuSearchSection skuLines={skuLines} setSkuLines={setSkuLines} editMode={!!editRow} />
              </div>

              {/* Vendor */}
              <div>
                <p style={sectionHead}>Vendor <span style={{ color: "#ef4444" }}>*</span></p>
                {partiesLoading ? (
                  <div style={{ padding: "12px", color: "#9ca3af", fontSize: 12 }}>Loading parties…</div>
                ) : (
                  <PartySelector
                    parties={allParties}
                    partyTypeFilter="vendor"
                    selected={selectedVendor}
                    onSelect={setSelectedVendor}
                    label="vendor"
                  />
                )}
              </div>

              {/* Delivery Party */}
              <div>
                <p style={sectionHead}>Delivery / Ship-to Party</p>
                {partiesLoading ? (
                  <div style={{ padding: "12px", color: "#9ca3af", fontSize: 12 }}>Loading parties…</div>
                ) : (
                  <PartySelector
                    parties={allParties}
                    partyTypeFilter=""
                    excludePartyType="vendor"
                    selected={selectedDelivery}
                    onSelect={setSelectedDelivery}
                    label="party"
                  />
                )}
              </div>

              {/* Dates */}
              <div>
                <p style={sectionHead}>Dates</p>
                <div className="rs-form-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                  <FormField label="Target Ship Date"><input style={inputStyle} type="date" value={form.target_ship_date} onChange={e => f("target_ship_date", e.target.value)} /></FormField>
                  <FormField label="Expected GRN Date"><input style={inputStyle} type="date" value={form.expected_grn_date} onChange={e => f("expected_grn_date", e.target.value)} /></FormField>
                  <FormField label="GRN Date (Actual)"><input style={inputStyle} type="date" value={form.grn_date} onChange={e => f("grn_date", e.target.value)} /></FormField>
                  <FormField label="Raised By"><input style={inputStyle} value={form.raised_by} onChange={e => f("raised_by", e.target.value)} /></FormField>
                </div>
              </div>

              <FormField label="Remarks">
                <textarea style={{ ...inputStyle, height: 72, resize: "vertical" }} value={form.remarks} onChange={e => f("remarks", e.target.value)} />
              </FormField>

            </div>

            {/* Panel footer */}
            <div className="rs-panel-footer" style={{ padding: "14px 24px", borderTop: "1px solid #e2e8f0", display: "flex", gap: 10, justifyContent: "flex-end", position: "sticky", bottom: 0, background: "#fff" }}>
              <button onClick={() => setModalOpen(false)} style={btnGhost}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={btnPrimary}>
                {saving ? "Saving…" : editRow ? "Update" : `Create${skuLines.length > 1 ? ` (${skuLines.length} SKUs)` : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
